import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);   // effective (subject when in view-as, else actor)
  const [actor, setActor] = useState(null); // real logged-in user (admin when in view-as)
  const [scopes, setScopes] = useState([]);
  const [readOnly, setReadOnly] = useState(false);
  const [privacy, setPrivacy] = useState(null); // real signed-in person's acknowledgement status
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    const fetchMe = async (retry) => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data.user);
        setScopes(data.scopes || []);
        setReadOnly(!!data.read_only);
        setActor(data.actor || null);
        setPrivacy(data.privacy || null);
        setPendingCount(data.pending_count || 0);
        return data.user;
      } catch (e) {
        // A rejected View-As target must not lock the real user out — drop it and retry once.
        if (retry && localStorage.getItem("atl_view_as") && e?.response?.status !== 401) {
          localStorage.removeItem("atl_view_as");
          return fetchMe(false);
        }
        setUser(null); setActor(null); setScopes([]); setReadOnly(false);
        return null;
      }
    };
    return fetchMe(true);
  }, []);

  useEffect(() => {
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const loginPassword = async (email, password) => {
    localStorage.removeItem("atl_view_as");
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("atl_token", data.access_token);
    await refresh();
    return data.user;
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("atl_token");
    localStorage.removeItem("atl_user");
    localStorage.removeItem("atl_view_as");
    setUser(null); setActor(null); setScopes([]); setReadOnly(false);
    window.location.href = "/login";
  };

  const startViewAs = async (targetUserId) => {
    localStorage.setItem("atl_view_as", targetUserId);
    await refresh();
    return localStorage.getItem("atl_view_as") === targetUserId;
  };

  const exitViewAs = async () => {
    localStorage.removeItem("atl_view_as");
    window.location.href = "/";
  };

  return (
    <AuthCtx.Provider value={{
      user, actor, scopes, loading, readOnly, privacy, pendingCount,
      isViewAs: readOnly,
      // read-only covers BOTH Admin View-As AND the oversight role so the
      // frontend can hide write affordances in a single check.
      isReadOnly: readOnly || user?.role === "oversight",
      refresh, loginPassword, logout,
      startViewAs, exitViewAs, setUser,
    }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
